import React, { useEffect } from 'react';
import Upload from '@uber/base-icons/upload';
import { styled } from 'baseui';
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover';
import { addMediaFiles, revokeMediaFileURLs } from '../utils/media-file-utils';
export type MediaFile = {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'pdf';
};
type MediaInputProps = {
  value: MediaFile[];
  onMediaChange: (files: MediaFile[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  acceptedFileTypes?: string[];
};
const MediaInputContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale300,
  flexShrink: 0
}));
const PopoverContent = styled('div', ({
  $theme
}) => ({
  ...$theme.typography.LabelSmall,
  padding: $theme.sizing.scale300,
  paddingTop: $theme.sizing.scale200,
  maxWidth: '300px',
  wordWrap: 'break-word',
  whiteSpace: 'normal'
}));
const LinkButton = styled('button', ({
  $theme
}) => ({
  ...$theme.typography.LabelSmall,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: `${$theme.sizing.scale100} 0`,
  color: $theme.colors.accent,
  display: 'inline-flex',
  alignItems: 'center',
  gap: $theme.sizing.scale100,
  textDecoration: 'none',
  ':hover': {
    textDecoration: 'underline'
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
    color: $theme.colors.contentSecondary
  }
}));
const INPUT_STYLES = {
  display: 'none'
};
export const MediaInput = ({
  value,
  onMediaChange,
  disabled = false,
  maxFiles = 5,
  acceptedFileTypes = ['image/png', 'image/jpeg', 'application/pdf', 'video/mp4']
}: MediaInputProps) => {
  const mediaFilesRef = React.useRef<MediaFile[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const handleFileAdd = (acceptedFiles: File[]) => {
    const updatedFiles = addMediaFiles(value, acceptedFiles, maxFiles);
    mediaFilesRef.current = updatedFiles;
    onMediaChange(updatedFiles);
  };
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      handleFileAdd(Array.from(files));
    }
  };
  useEffect(() => {
    return () => {
      revokeMediaFileURLs(mediaFilesRef.current);
    };
  }, []);
  return <MediaInputContainer>
      <input ref={fileInputRef} type="file" accept={acceptedFileTypes.join(', ')} multiple disabled={disabled} onChange={handleFileInputChange} style={INPUT_STYLES} />
      <StatefulPopover content={<PopoverContent>Insert PDF, image (PNG/JPEG) or video (MP4)</PopoverContent>} triggerType={TRIGGER_TYPE.hover} placement={PLACEMENT.bottom} popoverMargin={8} accessibilityType="tooltip" showArrow overrides={{
      Inner: {
        style: ({
          $theme
        }) => ({
          paddingTop: 0,
          backgroundColor: $theme.colors.backgroundPrimary
        })
      },
      Arrow: {
        style: ({
          $theme
        }) => ({
          top: 0,
          backgroundColor: $theme.colors.backgroundPrimary
        })
      }
    }}>
        <LinkButton onClick={handleButtonClick} disabled={disabled || value.length >= maxFiles} type="button">
          <Upload size={14} />
          Upload Files ({value.length}/{maxFiles})
        </LinkButton>
      </StatefulPopover>
    </MediaInputContainer>;
};